import React, { type RefObject, useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';
import type { AgGridReactProps, AgGridReact as AgGridReactType } from 'ag-grid-react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/agGridAlpineFont.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { GetTypography } from 'venn-ui-kit';
import useStickyHeader from './useStickyHeader';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any usage is to match type of AgGridReactProps
export interface DataGridProps<TData = any> extends AgGridReactProps<TData> {
  noCustomStickyHeader?: boolean;
  dataGridWrapperRef?: RefObject<HTMLDivElement>;
  gridRef?: RefObject<AgGridReactType<TData>>;
  className?: string;
  horizontalScrollbarHeight?: number;
}

export const GRID_COLUMN_HEADER_DATA_ATTR = 'data-grid-column-header';

const DataGrid = <TData,>({
  noCustomStickyHeader,
  dataGridWrapperRef,
  gridRef,
  className,
  horizontalScrollbarHeight,
  ...props
}: DataGridProps<TData>) => {
  useStickyHeader(noCustomStickyHeader);

  const markColumnHeaderElement = useCallback(() => {
    const wrapper = dataGridWrapperRef?.current;
    if (!wrapper) return;

    const header = wrapper.querySelector<HTMLElement>('.ag-header');
    if (header && !header.hasAttribute(GRID_COLUMN_HEADER_DATA_ATTR)) {
      header.setAttribute(GRID_COLUMN_HEADER_DATA_ATTR, '');
    }
  }, [dataGridWrapperRef]);

  useEffect(() => {
    markColumnHeaderElement();

    const wrapper = dataGridWrapperRef?.current;
    if (!wrapper) return undefined;

    const mutationObserver = new MutationObserver(() => {
      markColumnHeaderElement();
    });
    mutationObserver.observe(wrapper, { childList: true, subtree: true });

    return () => {
      mutationObserver.disconnect();
    };
  }, [dataGridWrapperRef, markColumnHeaderElement]);

  return (
    <DataGridWrapper
      className={className ?? 'ag-theme-alpine'}
      ref={dataGridWrapperRef}
      data-testid="datagrid-wrapper"
      horizontalScrollbarHeight={horizontalScrollbarHeight}
    >
      <AgGridReact ref={gridRef} {...props} />
    </DataGridWrapper>
  );
};

export default React.memo(DataGrid) as typeof DataGrid;

const DataGridWrapper = styled.div<{ horizontalScrollbarHeight?: number }>`
  width: 100%;
  height: 100%;

  --ag-font-family: ${GetTypography.fontFamily};

  .ag-root-wrapper {
    border: none;
  }

  .ag-center-cols-viewport,
  .ag-center-cols-clipper,
  .ag-center-cols-container {
    min-height: 35px !important;
  }

  /* 
  * ag grid doesn't allow us to customize the height of the horizontal scrollbar
  * so we work around this by overriding ag grid inline styles with !important
  * WARNING: this may break between ag grid updates
  */
  ${({ horizontalScrollbarHeight }) =>
    horizontalScrollbarHeight &&
    css`
      .ag-body-horizontal-scroll {
        height: ${horizontalScrollbarHeight}px !important;
        min-height: ${horizontalScrollbarHeight}px !important;
        max-height: ${horizontalScrollbarHeight}px !important;

        .ag-body-horizontal-scroll-viewport {
          height: ${horizontalScrollbarHeight}px !important;
          min-height: ${horizontalScrollbarHeight}px !important;
          max-height: ${horizontalScrollbarHeight}px !important;
        }
      }
    `}
`;
